import { AddressType, DecryptedAddress, DecryptedPropertyStub, DecryptedTelecom, DecryptedTypedValue, HealthcareParty, TelecomType } from '@icure/cardinal-sdk'
import { Form, Input, Select, Upload, UploadFile, UploadProps } from 'antd'
import ImgCrop from 'antd-img-crop'
import React, { ReactElement, useEffect, useState } from 'react'
import { useCreateOrUpdatePractitionerMutation } from '../../../core/api/practitionerApi'
import { getFileUploaderCommonProps, getImgSRC } from '../../../helpers/fileToBase64'

import { CustomModal } from '../../common/CustomModal'
import { SpinLoader } from '../../common/SpinLoader'
import './index.less'

interface ModalManageAccountFormProps {
  isVisible: boolean
  onClose: () => void
  practitionerToBeUpdated?: HealthcareParty
}

export const ModalManageAccountForm = ({ isVisible, onClose, practitionerToBeUpdated }: ModalManageAccountFormProps): ReactElement => {
  const [form] = Form.useForm()
  const [updatePractitioner, { isSuccess: isPractitionerUpdatedSuccessfully, isLoading: isPractitionerUpdatingLoading }] = useCreateOrUpdatePractitionerMutation()

  const userAvatarSrc = getImgSRC(practitionerToBeUpdated?.picture)

  const [patientPictureAsBase64, setPatientPictureAsBase64] = useState<Int8Array | undefined>(undefined)
  const [fileList, setFileList] = useState<UploadFile[]>(
    !userAvatarSrc
      ? []
      : [
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: userAvatarSrc,
          },
        ],
  )
  const handleSubmit = (value: {
    firstName: string
    lastName: string
    emailAddress: string
    speciality: string
    ssin: string
    fhcUrl: string
    hubId: string
    bingliEmailAddress: string
    bingliCredentials: string
    bingliTenantId: string
    bingliHealthcareServiceId: string
  }) => {
    const { firstName, lastName, emailAddress, speciality, ssin, bingliEmailAddress, bingliCredentials, bingliTenantId, bingliHealthcareServiceId, fhcUrl, hubId } = value
    const address = new DecryptedAddress({
      addressType: AddressType.Home,
      telecoms: [
        new DecryptedTelecom({
          telecomType: TelecomType.Email,
          telecomNumber: emailAddress,
        }),
      ],
    })
    const picture = patientPictureAsBase64 ?? practitionerToBeUpdated?.picture
    const properties = [
      new DecryptedPropertyStub({
        id: 'fhcUrl',
        typedValue: new DecryptedTypedValue({
          stringValue: fhcUrl,
        }),
      }),
      new DecryptedPropertyStub({
        id: 'hubId',
        typedValue: new DecryptedTypedValue({
          stringValue: hubId,
        }),
      }),
      new DecryptedPropertyStub({
        id: 'bingliEmailAddress',
        typedValue: new DecryptedTypedValue({
          stringValue: bingliEmailAddress,
        }),
      }),
      new DecryptedPropertyStub({
        id: 'bingliCredentials',
        typedValue: new DecryptedTypedValue({
          stringValue: bingliCredentials,
        }),
      }),
      new DecryptedPropertyStub({
        id: 'bingliTenantId',
        typedValue: new DecryptedTypedValue({
          stringValue: bingliTenantId,
        }),
      }),
      new DecryptedPropertyStub({
        id: 'bingliHealthcareServiceId',
        typedValue: new DecryptedTypedValue({
          stringValue: bingliHealthcareServiceId,
        }),
      }),
    ]
    updatePractitioner(new HealthcareParty({ ...practitionerToBeUpdated, firstName, lastName, speciality, ssin, properties, addresses: [address], picture }))
    form.resetFields()
  }

  useEffect(() => {
    if (isPractitionerUpdatedSuccessfully) {
      onClose()
    }
  }, [isPractitionerUpdatedSuccessfully])

  const practitionerEmail = practitionerToBeUpdated?.addresses[0].telecoms.find((item) => item.telecomType === TelecomType.Email)?.telecomNumber

  const fileUploaderProps: UploadProps = {
    listType: 'picture-card',
    multiple: false,
    showUploadList: {
      showRemoveIcon: true,
    },
    maxCount: 1,
    fileList: fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList)
    },
    onRemove() {
      setFileList([])
      setPatientPictureAsBase64(undefined)
    },
  }

  return (
    <CustomModal isVisible={isVisible} handleClose={onClose} secondaryBtnTitle="Cancel" handleClickPrimaryBtn={() => form.submit()} primaryBtnTitle="Save" title="Manage Account">
      <div className="modalManageAccountForm">
        {isPractitionerUpdatingLoading && <SpinLoader />}
        <Form
          className="modalManageAccountForm__form"
          layout="vertical"
          onFinish={(values) => handleSubmit(values)}
          colon={false}
          form={form}
          initialValues={{
            emailAddress: practitionerEmail,
            firstName: practitionerToBeUpdated?.firstName,
            lastName: practitionerToBeUpdated?.lastName,
            speciality: practitionerToBeUpdated?.speciality,
            ssin: practitionerToBeUpdated?.ssin,
            file: practitionerToBeUpdated?.picture,
            hubId: practitionerToBeUpdated?.properties?.find((p) => p.id === 'hubId')?.typedValue?.stringValue ?? 'rsw',
            fhcUrl: practitionerToBeUpdated?.properties?.find((p) => p.id === 'fhcUrl')?.typedValue?.stringValue ?? 'https://fhcprd.icure.cloud',
            bingliEmailAddress: practitionerToBeUpdated?.properties?.find((p) => p.id === 'bingliEmailAddress')?.typedValue?.stringValue,
            bingliCredentials: practitionerToBeUpdated?.properties?.find((p) => p.id === 'bingliCredentials')?.typedValue?.stringValue,
            bingliTenantId: practitionerToBeUpdated?.properties?.find((p) => p.id === 'bingliTenantId')?.typedValue?.stringValue,
            bingliHealthcareServiceId: practitionerToBeUpdated?.properties?.find((p) => p.id === 'bingliHealthcareServiceId')?.typedValue?.stringValue,
          }}
        >
          <div className="modalManageAccountForm__form__inputs">
            <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'First name is required' }]}>
              <Input placeholder="First name" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Last name is required' }]}>
              <Input placeholder="Last name" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="emailAddress" label="Email address" rules={[{ required: true, message: 'Email address is required' }]}>
              <Input placeholder="Email address" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="speciality" label="Speciality" rules={[{ required: true, message: 'Speciality is required' }]}>
              <Input placeholder="Speciality" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="ssin" label="SSIN">
              <Input placeholder="SSIN" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item label="Picture" valuePropName="file">
              <ImgCrop rotationSlider modalClassName="PatientImgCrop">
                <Upload {...fileUploaderProps} {...getFileUploaderCommonProps((data: Int8Array | undefined) => setPatientPictureAsBase64(data))}>
                  {fileList.length === 0 ? '+ Upload' : '+ Replace'}
                </Upload>
              </ImgCrop>
            </Form.Item>
            <Form.Item name="fhcUrl" label="Freehealth connector URL">
              <Input placeholder="Freehealth connector URL" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="hubId" label="Regional Hub">
              <Select
                size="large"
                style={{ fontSize: 13 }}
                options={['rsb', 'rsw', 'cozo', 'vitalink'].map((h) => {
                  return { value: h, label: h }
                })}
              />
            </Form.Item>
            <Form.Item name="bingliEmailAddress" label="Bingli Email address">
              <Input placeholder="Bingli Email address" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="bingliCredentials" label="Bingli Credentials">
              <Input type="password" placeholder="Bingli Credentials" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="bingliTenantId" label="Bingli Tenant Id">
              <Input placeholder="Bingli Tenant Id" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
            <Form.Item name="bingliHealthcareServiceId" label="Bingli HealthcareParty Id">
              <Input placeholder="Bingli HealthcareParty Id" size="large" style={{ fontSize: 13 }} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </CustomModal>
  )
}
